import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DataContext = createContext(null);

/**
 * Shared in-memory cache for users, departments and settings so pages
 * don't refetch the same data on every navigation.
 * Call `refreshUsers()`, `refreshDepts()`, or `refreshAll()` after a mutation.
 */
export const DataProvider = ({ children }) => {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [depts, setDepts] = useState([]);
    const [settings, setSettings] = useState(null);
    const [ready, setReady] = useState(false);
    const loaded = useRef(false);

    const refreshUsers = useCallback(async () => {
        const r = await api.get("/users");
        setUsers(r.data);
        return r.data;
    }, []);

    const refreshDepts = useCallback(async () => {
        const r = await api.get("/departments");
        setDepts(r.data);
        return r.data;
    }, []);

    const refreshSettings = useCallback(async () => {
        const r = await api.get("/settings");
        setSettings(r.data);
        return r.data;
    }, []);

    const refreshAll = useCallback(async () => {
        await Promise.all([refreshUsers(), refreshDepts(), refreshSettings()]);
    }, [refreshUsers, refreshDepts, refreshSettings]);

    useEffect(() => {
        if (!user || loaded.current) return;
        loaded.current = true;
        (async () => {
            await refreshAll();
            setReady(true);
        })();
    }, [user, refreshAll]);

    // Reset when user logs out
    useEffect(() => {
        if (!user) {
            loaded.current = false;
            setUsers([]); setDepts([]); setSettings(null); setReady(false);
        }
    }, [user]);

    return (
        <DataContext.Provider value={{ users, depts, settings, ready, refreshUsers, refreshDepts, refreshSettings, refreshAll, setUsers, setDepts }}>
            {children}
        </DataContext.Provider>
    );
};

export const useData = () => useContext(DataContext);
